import { create } from 'zustand';
import { log } from '@/lib/utils/logger';
import {
  ProjectDto,
  SampleDto,
  SampleActivityDto,
  ProjectActivityDto,
  PublicationDto,
  CommunicationDto,
  TheoryActivityDto,
  DataPackageDto,
  ShippingDto,
  FolderShareLink,
  InstrumentRunDto,
  BulkSampleDto,
  SampleSearchCriteria,
  ActivityTypeDto,
} from '@/api/ApiClient';

import { ProjectActivityType } from '@/lib/types/activityTypes';
import { useBackendStore } from '@/stores/backendStore';
import type { AddToInventoryDialogResult } from '@/types/addToInventory';

/**
 * Optional field defaults when opening Add Processing (e.g. after Add to inventory).
 */
export interface ProcessingDialogPrefill {
  date?: Date | null;
  technique?: { id: string; name: string } | null;
  description?: string;
  researcherUserId?: string;
}

export type BulkTransferDialogResult = {
  targetProjectLabels: string[];
  withoutActivity: boolean;
  reportingCategoryChangeConfirmed?: boolean;
};

export type BulkGrowthDialogResult = {
  date: Date;
  description?: string;
  instrument: string;
  label?: string;
  project: string;
  samples: number[];
  staff: string;
  growthMethod: string;
  reportingCategory?: string;
};

export type BulkGrowthDialogConfig = {
  samples: BulkSampleDto[];
  title?: string;
  availableTechniques?: Array<{ id: string; name: string }>;
  availableInstrumentsGetter?: (
    techniqueId: string
  ) => Array<{ id: string; name?: string; label?: string; value?: string }>;
  initialProject?: { id: number; label: string };
  initialTechnique?: string;
  validateBeforeSubmit?: (result: BulkGrowthDialogResult) => Promise<boolean>;
};

export type BulkSampleRecipeTemplateOption = { id: string; label: string; technique: string };

export type BulkSampleDialogConfig = {
  title?: string;
  initialProject?: { id: number; label: string };
  canChangeProject?: boolean;
  showSampleID?: boolean;
  /** When false, hide Precompounded Sample ID (caller provides precompounded list for multiple samples). Default true. */
  showPrecompoundedSample?: boolean;
  hideRecipeTemplate?: boolean;
  initialRecipeTemplate?: string;
  initialDescription?: string;
  initialSampleLabel?: string;
  initialTechnique?: string;
  availableRecipeTemplates?: BulkSampleRecipeTemplateOption[];
};

export type BulkSampleDialogResult = {
  date: Date;
  description?: string;
  /** Project label; empty when user has UnassignedSample and leaves project blank */
  project: string;
  sampleLabel?: string;
  staff: string;
  technique: string;
  template: string;
  precompounded?: string[];
};

export type BulkInstrumentRunDialogConfig = {
  title?: string;
  initialTechnique?: string;
  initialInstrument?: string;
  sampleLabel?: string;
};

export type BulkInstrumentRunDialogResult = {
  date: Date;
  instrument: string;
  technique: string;
};

export type BulkShippingSampleWithPiece = {
  sampleId: number;
  /** Empty/undefined = ship whole parent. Otherwise range/list, e.g. "1-3", "A,B,E". */
  pieceSpec?: string;
};

export type BulkShippingDialogConfig = {
  samples: BulkSampleDto[];
  projectId: number;
  projectLabel: string;
};

export type BulkShippingDialogResult = {
  samples: BulkShippingSampleWithPiece[];
  isLocalTransfer: boolean;
  comment?: string;
  /** Recipient fields (required when not local transfer) */
  budget?: string;
  shippingType?: string;
  researcherPhone?: string;
  returnAddress?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  researcher?: number;
  researcherEmail?: string;
  /** Local transfer date (ISO date string, yyyy-MM-dd) */
  transferDate?: string;
};

export type BulkShippingCreatedChoice = 'submit' | 'view' | 'close';

export type BulkShippingCreatedDialogConfig = {
  isLocalTransfer: boolean;
  canSubmit: boolean;
  submitDisabledReason?: string;
};

/** Where the Add Shipping flow was launched from (copy + sample picker defaults only). */
export type ShippingDialogLaunchFrom = 'projectTree' | 'shippingsPage';

/** Options when opening the global project picker from `showProjectSelectionDialog`. */
export type ProjectSelectionOpenOptions = {
  /** When false, the grid uses single-select semantics. Default true. */
  allowMultiple?: boolean;
};

/** Options when opening the global sample picker from `showSampleSelectionDialog`. */
export type SampleSelectionOpenOptions = {
  /** When false, no checkboxes; one row click selects and closes. Default true. */
  allowMultiple?: boolean;
};

/**
 * Configuration for confirm dialog
 */
export type ConfirmDialogConfig = {
  /** Title of the dialog */
  title: string;
  /** Message of the dialog */
  message: string;
  /** Text for the confirm button */
  confirmText?: string;
  /** Text for the dismiss/negative button (default: "Cancel") */
  cancelText?: string;
  /** Whether the action is destructive (will style the confirm button as error) */
  isDestructive?: boolean;
};

/**
 * Interface for confirm dialog configuration
 */
interface ConfirmConfig extends ConfirmDialogConfig {}

/**
 * Interface for dialog state and actions
 */
interface DialogState {
  // Global dialog states
  projectDialog: {
    isOpen: boolean;
    resolve?: (project: ProjectDto | null) => void;
    reject?: (reason?: any) => void;
  };

  confirmDialog: {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    isDestructive: boolean;
    resolve?: (confirmed: boolean) => void;
    reject?: (reason?: any) => void;
  };

  inventoryDialog: {
    project?: ProjectDto | undefined;
    isOpen: boolean;
    resolve?: (data: AddToInventoryDialogResult | null) => void;
    reject?: (reason?: any) => void;
  };

  combineStartingMaterialsDialog: {
    project?: ProjectDto | undefined;
    isOpen: boolean;
    resolve?: (sample: SampleDto | null) => void;
    reject?: (reason?: any) => void;
  };

  multiSampleGrowthDialog: {
    project?: ProjectDto | undefined;
    isOpen: boolean;
    resolve?: (growthRun: SampleDto | null) => void;
    reject?: (reason?: any) => void;
  };

  processingDialog: {
    isOpen: boolean;
    sample?: SampleDto;
    prefill?: ProcessingDialogPrefill;
    resolve?: (activity: SampleActivityDto | null) => void;
    reject?: (reason?: any) => void;
  };

  characterizationDialog: {
    isOpen: boolean;
    sample?: SampleDto;
    parentActivity?: SampleActivityDto;
    resolve?: (activity: SampleActivityDto | null) => void;
    reject?: (reason?: any) => void;
  };

  splitDialog: {
    isOpen: boolean;
    sample?: SampleDto;
    resolve?: (activity: SampleActivityDto | null) => void;
    reject?: (reason?: any) => void;
  };

  storageDialog: {
    parentNodeId?: string;
    isOpen: boolean;
    sample?: SampleDto;
    resolve?: (activity: SampleActivityDto | null) => void;
    reject?: (reason?: any) => void;
  };

  transferActivityDialog: {
    isOpen: boolean;
    sample?: SampleDto;
    isCurrentProjectInHouse?: boolean;
    resolve?: (activityIds: number[] | null) => void;
    reject?: (reason?: any) => void;
  };

  projectActivityDialog: {
    isOpen: boolean;
    project?: ProjectDto;
    activityType?: ActivityTypeDto;
    resolve?: (activity: ProjectActivityDto | null) => void;
    reject?: (reason?: any) => void;
  };

  createPublicationDialog: {
    isOpen: boolean;
    project?: ProjectDto;
    resolve?: (result: ProjectActivityDto | PublicationDto | null) => void;
    reject?: (reason?: any) => void;
  };

  communicationDialog: {
    isOpen: boolean;
    project?: ProjectDto;
    resolve?: (communication: CommunicationDto | null) => void;
    reject?: (reason?: any) => void;
  };

  shippingDialog: {
    isOpen: boolean;
    project?: ProjectDto;
    returnAddress?: string;
    launchFrom?: ShippingDialogLaunchFrom;
    resolve?: (shipping: ShippingDto | null) => void;
    reject?: (reason?: any) => void;
  };

  addTheoryActivityDialog: {
    isOpen: boolean;
    dataPackage?: DataPackageDto;
    resolve?: (activity: TheoryActivityDto | null) => void;
    reject?: (reason?: any) => void;
  };

  addSimulationDatasetDialog: {
    isOpen: boolean;
    project?: ProjectDto;
    resolve?: (dataPackage: DataPackageDto | null) => void;
    reject?: (reason?: any) => void;
  };

  addDataPackageDialog: {
    isOpen: boolean;
    resolve?: (dataPackage: DataPackageDto | null) => void;
    reject?: (reason?: any) => void;
  };

  sampleSelectionDialog: {
    isOpen: boolean;
    title?: string;
    disabledSampleIds?: number[];
    /** Merged with defaults when the dialog opens; user can change filters afterward. */
    initialFilters?: Partial<SampleSearchCriteria>;
    /** When false, one row click selects and closes (no checkboxes). Default true. */
    allowMultiple?: boolean;
    resolve?: (samples: SampleDto[] | null) => void;
    reject?: (reason?: any) => void;
  };

  theoryActivitySelectionDialog: {
    isOpen: boolean;
    title?: string;
    resolve?: (activities: TheoryActivityDto[] | null) => void;
    reject?: (reason?: any) => void;
  };

  projectSelectionDialog: {
    isOpen: boolean;
    title?: string;
    allowMultiple?: boolean;
    resolve?: (projects: ProjectDto[] | null) => void;
    reject?: (reason?: any) => void;
  };

  doiDialog: {
    isOpen: boolean;
    dataPackage?: DataPackageDto;
    resolve?: (result: { dataPackageDoi?: string; publicationDoi?: string } | null) => void;
    reject?: (reason?: any) => void;
  };

  downloadFolderDialog: {
    isOpen: boolean;
    folderName?: string;
    initialLinkResult?: FolderShareLink;
    onPollForUpdates?: () => Promise<FolderShareLink>;
    resolve?: () => void;
    reject?: (reason?: any) => void;
  };

  bulkRecipeEditorDialog: {
    isOpen: boolean;
    activityId?: number;
    activityType?: 'preparation' | 'synthesis' | 'compounding';
    startingMaterialNumber?: 1 | 2;
    sample?: SampleDto;
    mode?: 'view' | 'edit';
    /** When true, recipe is view-only (viewing precursor prep from a growth row). */
    forceReadOnly?: boolean;
    /** Precompounded precursor sample label (starting material owner). */
    startingMaterialSampleLabel?: string;
    /** Bulk grid row sample label from which the dialog was opened. */
    viewingSampleLabel?: string;
    /** Project collaborator role ids for the preparation owner row (precursor sample). */
    preparationOwnerRoles?: number[];
    resolve?: (result: InstrumentRunDto | SampleActivityDto | BulkSampleDto | null) => void;
    reject?: (reason?: any) => void;
  };

  bulkTransferDialog: {
    isOpen: boolean;
    samples?: BulkSampleDto[];
    isCurrentProjectInHouse?: boolean;
    resolve?: (result: BulkTransferDialogResult | null) => void;
    reject?: (reason?: any) => void;
  };

  bulkGrowthDialog: {
    isOpen: boolean;
    samples?: BulkSampleDto[];
    title?: string;
    availableTechniques?: Array<{ id: string; name: string }>;
    availableInstrumentsGetter?: BulkGrowthDialogConfig['availableInstrumentsGetter'];
    initialProject?: { id: number; label: string };
    initialTechnique?: string;
    validateBeforeSubmit?: BulkGrowthDialogConfig['validateBeforeSubmit'];
    resolve?: (result: BulkGrowthDialogResult | null) => void;
    reject?: (reason?: any) => void;
  };

  bulkSampleDialog: {
    isOpen: boolean;
    title?: string;
    initialProject?: { id: number; label: string };
    canChangeProject?: boolean;
    showSampleID?: boolean;
    showPrecompoundedSample?: boolean;
    hideRecipeTemplate?: boolean;
    initialRecipeTemplate?: string;
    initialDescription?: string;
    initialSampleLabel?: string;
    initialTechnique?: string;
    availableRecipeTemplates?: BulkSampleRecipeTemplateOption[];
    resolve?: (result: BulkSampleDialogResult | null) => void;
    reject?: (reason?: any) => void;
  };

  bulkInstrumentRunDialog: {
    isOpen: boolean;
    title?: string;
    initialTechnique?: string;
    initialInstrument?: string;
    sampleLabel?: string;
    resolve?: (result: BulkInstrumentRunDialogResult | null) => void;
    reject?: (reason?: any) => void;
  };

  bulkShippingDialog: {
    isOpen: boolean;
    samples?: BulkSampleDto[];
    projectId?: number;
    projectLabel?: string;
    resolve?: (result: BulkShippingDialogResult | null) => void;
    reject?: (reason?: any) => void;
  };

  bulkShippingCreatedDialog: {
    isOpen: boolean;
    isLocalTransfer?: boolean;
    canSubmit?: boolean;
    submitDisabledReason?: string;
    resolve?: (choice: BulkShippingCreatedChoice) => void;
    reject?: (reason?: any) => void;
  };

  // Footer-specific dialogs
  submitDialog: {
    isOpen: boolean;
    nodeType?: string;
    nodeTypeLabel?: string;
    nodeId?: string;
    resolve?: (confirmed: boolean) => void;
    reject?: (reason?: any) => void;
  };

  resetDialog: {
    isOpen: boolean;
    nodeType?: string;
    nodeId?: string;
    resolve?: (confirmed: boolean) => void;
    reject?: (reason?: any) => void;
  };

  deleteDialog: {
    isOpen: boolean;
    nodeType?: string;
    nodeId?: string;
    resolve?: (confirmed: boolean) => void;
    reject?: (reason?: any) => void;
  };

  // Global dialog actions
  showAddProjectDialog: () => Promise<ProjectDto | null>;
  showConfirmDialog: (config: ConfirmConfig) => Promise<boolean>;
  showAddToInventoryDialog: (project?: ProjectDto) => Promise<AddToInventoryDialogResult | null>;
  showCombineStartingMaterialsDialog: (project?: ProjectDto) => Promise<SampleDto | null>;
  showMultiSampleGrowthDialog: (project?: ProjectDto) => Promise<SampleDto | null>;
  showProcessingDialog: (sample: SampleDto, prefill?: ProcessingDialogPrefill) => Promise<SampleActivityDto | null>;
  showCharacterizationDialog: (
    sample: SampleDto,
    parentActivity?: SampleActivityDto
  ) => Promise<SampleActivityDto | null>;
  showSplitDialog: (sample: SampleDto) => Promise<SampleActivityDto | null>;
  showStorageDialog: (sample: SampleDto) => Promise<SampleActivityDto | null>;
  showTransferActivityDialog: (sample: SampleDto) => Promise<number[] | null>;
  showProjectActivityDialog: (
    project: ProjectDto,
    activityType?: ProjectActivityType
  ) => Promise<ProjectActivityDto | null>;
  showCreatePublicationDialog: (project?: ProjectDto) => Promise<ProjectActivityDto | PublicationDto | null>;
  showCommunicationDialog: (project: ProjectDto) => Promise<CommunicationDto | null>;
  showSubmitDialog: (nodeType: string, nodeId: string) => Promise<boolean>;
  showResetDialog: (nodeType: string, nodeId: string) => Promise<boolean>;
  showDeleteDialog: (nodeType: string, nodeId: string) => Promise<boolean>;
  showAddTheoryActivityDialog: (dataPackage: DataPackageDto) => Promise<TheoryActivityDto | null>;
  showAddSimulationDatasetDialog: (project: ProjectDto) => Promise<DataPackageDto | null>;
  showAddDataPackageDialog: () => Promise<DataPackageDto | null>;
  showAddShippingDialog: (
    project?: ProjectDto | null,
    options?: { launchFrom?: ShippingDialogLaunchFrom }
  ) => Promise<ShippingDto | null>;
  showSampleSelectionDialog: (
    title?: string,
    disabledSampleIds?: number[],
    initialFilters?: Partial<SampleSearchCriteria>,
    options?: SampleSelectionOpenOptions
  ) => Promise<SampleDto[] | null>;
  showTheoryActivitySelectionDialog: (title?: string) => Promise<TheoryActivityDto[] | null>;
  showProjectSelectionDialog: (title?: string, options?: ProjectSelectionOpenOptions) => Promise<ProjectDto[] | null>;
  showDoiDialog: (dataPackage: DataPackageDto) => Promise<{ dataPackageDoi?: string; publicationDoi?: string } | null>;
  showDownloadFolderDialog: (
    folderName: string,
    initialLinkResult: FolderShareLink,
    onPollForUpdates?: () => Promise<FolderShareLink>
  ) => Promise<void>;
  showBulkRecipeEditorDialog: (
    activityId: number,
    activityType: 'preparation' | 'synthesis' | 'compounding',
    sample?: SampleDto,
    startingMaterialNumber?: 1 | 2,
    mode?: 'view' | 'edit',
    forceReadOnly?: boolean,
    startingMaterialSampleLabel?: string,
    viewingSampleLabel?: string,
    preparationOwnerRoles?: number[]
  ) => Promise<InstrumentRunDto | SampleActivityDto | BulkSampleDto | null>;

  showBulkTransferDialog: (
    samples: BulkSampleDto[],
    isCurrentProjectInHouse?: boolean
  ) => Promise<BulkTransferDialogResult | null>;

  showBulkGrowthDialog: (config: BulkGrowthDialogConfig) => Promise<BulkGrowthDialogResult | null>;

  showBulkSampleDialog: (config: BulkSampleDialogConfig) => Promise<BulkSampleDialogResult | null>;

  showBulkInstrumentRunDialog: (config: BulkInstrumentRunDialogConfig) => Promise<BulkInstrumentRunDialogResult | null>;

  showBulkShippingDialog: (config: BulkShippingDialogConfig) => Promise<BulkShippingDialogResult | null>;

  showBulkShippingCreatedDialog: (config: BulkShippingCreatedDialogConfig) => Promise<BulkShippingCreatedChoice>;

  closeProjectDialog: (project?: ProjectDto) => void;
  closeConfirmDialog: (confirmed: boolean) => void;
  closeInventoryDialog: (data?: AddToInventoryDialogResult | null) => void;
  closeSampleFromStartingMaterialsDialog: (data?: SampleDto) => void;
  closeMultiSampleGrowthDialog: (data?: SampleDto) => void;
  closeProcessingDialog: (activity?: SampleActivityDto) => void;
  closeCharacterizationDialog: (activity?: SampleActivityDto) => void;
  closeSplitDialog: (activity?: SampleActivityDto) => void;
  closeStorageDialog: (activity?: SampleActivityDto) => void;
  closeTransferActivityDialog: (activityIds?: number[]) => void;
  closeProjectActivityDialog: (activity?: ProjectActivityDto) => void;
  closeCreatePublicationDialog: (result?: ProjectActivityDto | PublicationDto) => void;
  closeCommunicationDialog: (communication?: CommunicationDto) => void;
  closeShippingDialog: (shipping?: ShippingDto) => void;
  closeSubmitDialog: (confirmed: boolean) => void;
  closeResetDialog: (confirmed: boolean) => void;
  closeDeleteDialog: (confirmed: boolean) => void;
  closeAddTheoryActivityDialog: (activity?: TheoryActivityDto) => void;
  closeAddSimulationDatasetDialog: (dataPackage?: DataPackageDto) => void;
  closeAddDataPackageDialog: (dataPackage?: DataPackageDto) => void;
  closeSampleSelectionDialog: (samples?: SampleDto[]) => void;
  closeTheoryActivitySelectionDialog: (activities?: TheoryActivityDto[]) => void;
  closeProjectSelectionDialog: (projects?: ProjectDto[]) => void;
  closeDoiDialog: (result?: { dataPackageDoi?: string; publicationDoi?: string } | null) => void;
  closeDownloadFolderDialog: () => void;
  closeBulkRecipeEditorDialog: (result?: InstrumentRunDto | SampleActivityDto | BulkSampleDto | null) => void;

  closeBulkTransferDialog: (result?: BulkTransferDialogResult | null) => void;

  closeBulkGrowthDialog: (result?: BulkGrowthDialogResult | null) => void;

  closeBulkSampleDialog: (result?: BulkSampleDialogResult | null) => void;

  closeBulkInstrumentRunDialog: (result?: BulkInstrumentRunDialogResult | null) => void;

  closeBulkShippingDialog: (result?: BulkShippingDialogResult | null) => void;
  closeBulkShippingCreatedDialog: (choice?: BulkShippingCreatedChoice) => void;
}

/**
 * Creates a dialog store for managing dialog state and interactions
 */
export const useDialogStore = create<DialogState>((set, get) => ({
  // Global dialog initial states
  projectDialog: {
    isOpen: false,
  },

  confirmDialog: {
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    isDestructive: false,
  },

  inventoryDialog: {
    isOpen: false,
  },

  combineStartingMaterialsDialog: {
    isOpen: false,
  },

  multiSampleGrowthDialog: {
    isOpen: false,
  },

  processingDialog: {
    isOpen: false,
  },

  characterizationDialog: {
    isOpen: false,
  },

  splitDialog: {
    isOpen: false,
  },

  storageDialog: {
    isOpen: false,
  },

  transferActivityDialog: {
    isOpen: false,
  },

  projectActivityDialog: {
    isOpen: false,
  },

  createPublicationDialog: {
    isOpen: false,
  },

  communicationDialog: {
    isOpen: false,
  },

  shippingDialog: {
    isOpen: false,
  },

  addTheoryActivityDialog: {
    isOpen: false,
  },

  addSimulationDatasetDialog: {
    isOpen: false,
  },

  addDataPackageDialog: {
    isOpen: false,
  },

  sampleSelectionDialog: {
    isOpen: false,
  },

  theoryActivitySelectionDialog: {
    isOpen: false,
  },

  projectSelectionDialog: {
    isOpen: false,
  },

  doiDialog: {
    isOpen: false,
  },

  downloadFolderDialog: {
    isOpen: false,
  },

  bulkRecipeEditorDialog: {
    isOpen: false,
  },

  bulkTransferDialog: {
    isOpen: false,
  },

  bulkGrowthDialog: {
    isOpen: false,
  },

  bulkSampleDialog: {
    isOpen: false,
  },

  bulkInstrumentRunDialog: {
    isOpen: false,
  },

  bulkShippingDialog: {
    isOpen: false,
  },

  bulkShippingCreatedDialog: {
    isOpen: false,
  },

  submitDialog: {
    isOpen: false,
  },

  resetDialog: {
    isOpen: false,
  },

  deleteDialog: {
    isOpen: false,
  },

  // Global dialog actions
  showAddProjectDialog: async () => {
    log.debug('[DialogStore] Showing add project dialog');

    return new Promise<ProjectDto | null>((resolve, reject) => {
      set({
        projectDialog: {
          isOpen: true,
          resolve,
          reject,
        },
      });
    });
  },

  showConfirmDialog: async (config: ConfirmConfig) => {
    log.debug('[DialogStore] Showing confirm dialog:', config);

    return new Promise<boolean>((resolve, reject) => {
      set({
        confirmDialog: {
          isOpen: true,
          title: config.title,
          message: config.message,
          confirmText: config.confirmText || 'Confirm',
          cancelText: config.cancelText ?? 'Cancel',
          isDestructive: config.isDestructive || false,
          resolve,
          reject,
        },
      });
    });
  },

  showAddToInventoryDialog: async (project?: ProjectDto) => {
    log.debug('[DialogStore] Showing add to inventory dialog', { project: project?.projectLabel });

    return new Promise<AddToInventoryDialogResult | null>((resolve, reject) => {
      set({
        inventoryDialog: {
          project,
          isOpen: true,
          resolve,
          reject,
        },
      });
    });
  },

  showCombineStartingMaterialsDialog: async () => {
    log.debug('[DialogStore] Showing combine starting materials dialog');

    return new Promise<SampleDto | null>((resolve, reject) => {
      set({
        combineStartingMaterialsDialog: {
          isOpen: true,
          resolve,
          reject,
        },
      });
    });
  },

  showMultiSampleGrowthDialog: async (project?: ProjectDto) => {
    log.debug('[DialogStore] Showing multi-sample growth dialog');

    return new Promise<SampleDto | null>((resolve, reject) => {
      set({
        multiSampleGrowthDialog: {
          project,
          isOpen: true,
          resolve,
          reject,
        },
      });
    });
  },

  closeProjectDialog: (project?: ProjectDto) => {
    log.debug('[DialogStore] Closing project dialog:', project);
    const { projectDialog } = get();

    if (projectDialog.resolve) {
      projectDialog.resolve(project || null);
    }

    set({
      projectDialog: {
        isOpen: false,
      },
    });
  },

  closeConfirmDialog: (confirmed: boolean) => {
    log.debug('[DialogStore] Closing confirm dialog:', confirmed);
    const { confirmDialog } = get();

    if (confirmDialog.resolve) {
      confirmDialog.resolve(confirmed);
    }

    set({
      confirmDialog: {
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        isDestructive: false,
      },
    });
  },

  closeInventoryDialog: (data?: AddToInventoryDialogResult | null) => {
    log.debug('[DialogStore] Closing inventory dialog:', data);
    const { inventoryDialog } = get();

    if (inventoryDialog.resolve) {
      inventoryDialog.resolve(data || null);
    }

    set({
      inventoryDialog: {
        isOpen: false,
      },
    });
  },

  closeSampleFromStartingMaterialsDialog: (data?: SampleDto | null) => {
    log.debug('[DialogStore] Closing combine starting materials dialog:', data);
    const { combineStartingMaterialsDialog } = get();

    if (combineStartingMaterialsDialog.resolve) {
      combineStartingMaterialsDialog.resolve(data || null);
    }

    set({
      combineStartingMaterialsDialog: {
        isOpen: false,
      },
    });
  },

  closeMultiSampleGrowthDialog: (data?: SampleDto) => {
    log.debug('[DialogStore] Closing multi-sample growth dialog');
    const { multiSampleGrowthDialog } = get();

    if (multiSampleGrowthDialog.resolve) {
      multiSampleGrowthDialog.resolve(data ?? null);
    }

    set({
      multiSampleGrowthDialog: {
        isOpen: false,
      },
    });
  },

  showProcessingDialog: async (sample: SampleDto, prefill?: ProcessingDialogPrefill) => {
    log.debug('[DialogStore] Showing processing dialog', { hasPrefill: !!prefill });

    return new Promise<SampleActivityDto | null>((resolve, reject) => {
      set({
        processingDialog: {
          isOpen: true,
          sample,
          prefill,
          resolve,
          reject,
        },
      });
    });
  },

  showCharacterizationDialog: async (sample: SampleDto, parentActivity?: SampleActivityDto) => {
    log.debug('[DialogStore] Showing characterization dialog');

    if (!sample) {
      throw new Error('Sample is required');
    }

    return new Promise<SampleActivityDto | null>((resolve, reject) => {
      set({
        characterizationDialog: {
          isOpen: true,
          sample,
          parentActivity,
          resolve,
          reject,
        },
      });
    });
  },

  showSplitDialog: async (sample: SampleDto) => {
    log.debug('[DialogStore] Showing split dialog');

    return new Promise<SampleActivityDto | null>((resolve, reject) => {
      set({
        splitDialog: {
          isOpen: true,
          sample,
          resolve,
          reject,
        },
      });
    });
  },

  showStorageDialog: async (sample: SampleDto, sampleNodeId?: string) => {
    log.debug('[DialogStore] Showing storage dialog');

    return new Promise<SampleActivityDto | null>((resolve, reject) => {
      set({
        storageDialog: {
          parentNodeId: sampleNodeId,
          isOpen: true,
          sample,
          resolve,
          reject,
        },
      });
    });
  },

  showTransferActivityDialog: async (sample: SampleDto) => {
    log.debug('[DialogStore] Showing transfer activity dialog');

    // Pre-compute isCurrentProjectInHouse before opening dialog
    let isCurrentProjectInHouse: boolean | null = null;
    if (sample?.projectIds && sample.projectIds.length > 0) {
      try {
        const backend = useBackendStore.getState().getBackend();
        const projectPromises = sample.projectIds.map((id) => backend.project.get(id.toString()));
        const projects = await Promise.all(projectPromises);
        const hasNonInHouseProject = projects.some((project) => !project.type?.isInternal);
        // Show checkbox only if there are NO non-in-house projects (i.e., all projects are in-house)
        isCurrentProjectInHouse = !hasNonInHouseProject;
      } catch (error) {
        log.error('[DialogStore] Failed to get project types for transfer dialog:', error);
        isCurrentProjectInHouse = null;
      }
    } else {
      // No projects - user has choice
      isCurrentProjectInHouse = true;
    }

    return new Promise<number[] | null>((resolve, reject) => {
      set({
        transferActivityDialog: {
          isOpen: true,
          sample,
          isCurrentProjectInHouse: isCurrentProjectInHouse ?? false,
          resolve,
          reject,
        },
      });
    });
  },

  showProjectActivityDialog: async (project: ProjectDto, activityType?: ProjectActivityType) => {
    log.debug('[DialogStore] Showing project activity dialog');

    return new Promise<ProjectActivityDto | null>((resolve, reject) => {
      set({
        projectActivityDialog: {
          isOpen: true,
          project,
          activityType,
          resolve,
          reject,
        },
      });
    });
  },

  showCreatePublicationDialog: async (project?: ProjectDto) => {
    log.debug('[DialogStore] Showing create publication dialog');
    return new Promise<ProjectActivityDto | PublicationDto | null>((resolve, reject) => {
      set({
        createPublicationDialog: {
          isOpen: true,
          project,
          resolve,
          reject,
        },
      });
    });
  },

  showCommunicationDialog: async (project: ProjectDto) => {
    log.debug('[DialogStore] Showing communication dialog');

    return new Promise<CommunicationDto | null>((resolve, reject) => {
      set({
        communicationDialog: {
          isOpen: true,
          project,
          resolve,
          reject,
        },
      });
    });
  },

  showAddTheoryActivityDialog: async (dataPackage: DataPackageDto) => {
    log.debug('[DialogStore] Showing add theory activity dialog');

    return new Promise<TheoryActivityDto | null>((resolve, reject) => {
      set({
        addTheoryActivityDialog: {
          isOpen: true,
          dataPackage,
          resolve,
          reject,
        },
      });
    });
  },

  showAddSimulationDatasetDialog: async (project: ProjectDto) => {
    log.debug('[DialogStore] Showing add simulation dataset dialog');

    return new Promise<DataPackageDto | null>((resolve, reject) => {
      set({
        addSimulationDatasetDialog: {
          isOpen: true,
          project,
          resolve,
          reject,
        },
      });
    });
  },

  showAddDataPackageDialog: async () => {
    log.debug('[DialogStore] Showing add data package dialog');

    return new Promise<DataPackageDto | null>((resolve, reject) => {
      set({
        addDataPackageDialog: {
          isOpen: true,
          resolve,
          reject,
        },
      });
    });
  },

  showAddShippingDialog: async (project?: ProjectDto | null, options?: { launchFrom?: ShippingDialogLaunchFrom }) => {
    log.debug('[DialogStore] Showing add shipping dialog', {
      project: project?.projectLabel ?? '(none)',
      launchFrom: options?.launchFrom ?? 'projectTree',
    });

    // Pre-load return address before opening dialog
    let returnAddress = '';
    try {
      const backend = useBackendStore.getState().getBackend();
      returnAddress = await backend.shipping.getReturnAddress();
    } catch (error) {
      log.error('[DialogStore] Failed to load return address for shipping dialog:', error);
      // Continue without return address - it's not critical for creation
    }

    return new Promise<ShippingDto | null>((resolve, reject) => {
      set({
        shippingDialog: {
          isOpen: true,
          project: project ?? undefined,
          returnAddress,
          launchFrom: options?.launchFrom ?? 'projectTree',
          resolve,
          reject,
        },
      });
    });
  },

  showSampleSelectionDialog: async (
    title?: string,
    disabledSampleIds?: number[],
    initialFilters?: Partial<SampleSearchCriteria>,
    options?: SampleSelectionOpenOptions
  ) => {
    log.debug('[DialogStore] Showing sample selection dialog');

    return new Promise<SampleDto[] | null>((resolve, reject) => {
      set({
        sampleSelectionDialog: {
          isOpen: true,
          title,
          disabledSampleIds,
          initialFilters,
          // Default true: data package / combine materials need multi-select checkboxes.
          allowMultiple: options?.allowMultiple ?? true,
          resolve,
          reject,
        },
      });
    });
  },

  showTheoryActivitySelectionDialog: async (title?: string) => {
    log.debug('[DialogStore] Showing theory activity selection dialog');

    return new Promise<TheoryActivityDto[] | null>((resolve, reject) => {
      set({
        theoryActivitySelectionDialog: {
          isOpen: true,
          title,
          resolve,
          reject,
        },
      });
    });
  },

  closeProcessingDialog: (activity?: SampleActivityDto) => {
    log.debug('[DialogStore] Closing processing dialog:', activity);
    const { processingDialog } = get();

    if (processingDialog.resolve) {
      processingDialog.resolve(activity || null);
    }

    set({
      processingDialog: {
        isOpen: false,
        prefill: undefined,
      },
    });
  },

  closeCharacterizationDialog: (activity?: SampleActivityDto) => {
    log.debug('[DialogStore] Closing characterization dialog:', activity);
    const { characterizationDialog } = get();

    if (characterizationDialog.resolve) {
      characterizationDialog.resolve(activity || null);
    }

    set({
      characterizationDialog: {
        isOpen: false,
      },
    });
  },

  closeSplitDialog: (activity?: SampleActivityDto) => {
    log.debug('[DialogStore] Closing split dialog:', activity);
    const { splitDialog } = get();

    if (splitDialog.resolve) {
      splitDialog.resolve(activity || null);
    }

    set({
      splitDialog: {
        isOpen: false,
      },
    });
  },

  closeStorageDialog: (activity?: SampleActivityDto) => {
    log.debug('[DialogStore] Closing storage dialog:', activity);
    const { storageDialog } = get();

    if (storageDialog.resolve) {
      storageDialog.resolve(activity || null);
    }

    set({
      storageDialog: {
        isOpen: false,
      },
    });
  },

  closeTransferActivityDialog: (activityIds?: number[]) => {
    log.debug('[DialogStore] Closing transfer activity dialog:', activityIds);
    const { transferActivityDialog } = get();

    if (transferActivityDialog.resolve) {
      transferActivityDialog.resolve(activityIds || null);
    }

    set({
      transferActivityDialog: {
        isOpen: false,
      },
    });
  },

  closeProjectActivityDialog: (activity?: ProjectActivityDto) => {
    log.debug('[DialogStore] Closing project activity dialog:', activity);
    const { projectActivityDialog } = get();

    if (projectActivityDialog.resolve) {
      projectActivityDialog.resolve(activity || null);
    }

    set({
      projectActivityDialog: {
        isOpen: false,
      },
    });
  },

  closeCreatePublicationDialog: (result?: ProjectActivityDto | PublicationDto) => {
    log.debug('[DialogStore] Closing create publication dialog:', result);
    const { createPublicationDialog } = get();
    if (createPublicationDialog.resolve) {
      createPublicationDialog.resolve(result || null);
    }
    set({
      createPublicationDialog: {
        isOpen: false,
      },
    });
  },

  closeCommunicationDialog: (communication?: CommunicationDto) => {
    log.debug('[DialogStore] Closing communication dialog:', communication);
    const { communicationDialog } = get();

    if (communicationDialog.resolve) {
      communicationDialog.resolve(communication || null);
    }

    set({
      communicationDialog: {
        isOpen: false,
      },
    });
  },

  closeShippingDialog: (shipping?: ShippingDto) => {
    log.debug('[DialogStore] Closing shipping dialog:', shipping);
    const { shippingDialog } = get();

    if (shippingDialog.resolve) {
      shippingDialog.resolve(shipping || null);
    }

    set({
      shippingDialog: {
        isOpen: false,
      },
    });
  },

  closeAddTheoryActivityDialog: (activity?: TheoryActivityDto) => {
    log.debug('[DialogStore] Closing add theory activity dialog:', activity);
    const { addTheoryActivityDialog } = get();

    if (addTheoryActivityDialog.resolve) {
      addTheoryActivityDialog.resolve(activity || null);
    }

    set({
      addTheoryActivityDialog: {
        isOpen: false,
      },
    });
  },

  closeAddSimulationDatasetDialog: (dataPackage?: DataPackageDto) => {
    log.debug('[DialogStore] Closing add simulation dataset dialog:', dataPackage);
    const { addSimulationDatasetDialog } = get();

    if (addSimulationDatasetDialog.resolve) {
      addSimulationDatasetDialog.resolve(dataPackage || null);
    }

    set({
      addSimulationDatasetDialog: {
        isOpen: false,
      },
    });
  },

  closeAddDataPackageDialog: (dataPackage?: DataPackageDto) => {
    log.debug('[DialogStore] Closing add data package dialog:', dataPackage);
    const { addDataPackageDialog } = get();

    if (addDataPackageDialog.resolve) {
      addDataPackageDialog.resolve(dataPackage || null);
    }

    set({
      addDataPackageDialog: {
        isOpen: false,
      },
    });
  },

  closeSampleSelectionDialog: (samples?: SampleDto[]) => {
    log.debug('[DialogStore] Closing sample selection dialog:', samples);
    const { sampleSelectionDialog } = get();

    if (sampleSelectionDialog.resolve) {
      sampleSelectionDialog.resolve(samples || null);
    }

    set({
      sampleSelectionDialog: {
        isOpen: false,
        initialFilters: undefined,
        allowMultiple: undefined,
      },
    });
  },

  closeTheoryActivitySelectionDialog: (activities?: TheoryActivityDto[]) => {
    log.debug('[DialogStore] Closing theory activity selection dialog:', activities);
    const { theoryActivitySelectionDialog } = get();

    if (theoryActivitySelectionDialog.resolve) {
      theoryActivitySelectionDialog.resolve(activities || null);
    }

    set({
      theoryActivitySelectionDialog: {
        isOpen: false,
      },
    });
  },

  showProjectSelectionDialog: async (title?: string, options?: ProjectSelectionOpenOptions) => {
    log.debug('[DialogStore] Showing project selection dialog');

    return new Promise<ProjectDto[] | null>((resolve, reject) => {
      set({
        projectSelectionDialog: {
          isOpen: true,
          title,
          allowMultiple: options?.allowMultiple ?? true,
          resolve,
          reject,
        },
      });
    });
  },

  closeProjectSelectionDialog: (projects?: ProjectDto[]) => {
    log.debug('[DialogStore] Closing project selection dialog:', projects);
    const { projectSelectionDialog } = get();

    if (projectSelectionDialog.resolve) {
      projectSelectionDialog.resolve(projects || null);
    }

    set({
      projectSelectionDialog: {
        isOpen: false,
      },
    });
  },

  showDoiDialog: async (dataPackage: DataPackageDto) => {
    log.debug('[DialogStore] Showing DOI dialog');

    return new Promise<{ dataPackageDoi?: string; publicationDoi?: string } | null>((resolve, reject) => {
      set({
        doiDialog: {
          isOpen: true,
          dataPackage,
          resolve,
          reject,
        },
      });
    });
  },

  showDownloadFolderDialog: async (
    folderName: string,
    initialLinkResult: FolderShareLink,
    onPollForUpdates?: () => Promise<FolderShareLink>
  ) => {
    log.debug('[DialogStore] Showing share folder dialog', { folderName });

    return new Promise<void>((resolve, reject) => {
      set({
        downloadFolderDialog: {
          isOpen: true,
          folderName,
          initialLinkResult,
          onPollForUpdates,
          resolve,
          reject,
        },
      });
    });
  },

  closeDoiDialog: (result?: { dataPackageDoi?: string; publicationDoi?: string } | null) => {
    log.debug('[DialogStore] Closing DOI dialog:', result);
    const { doiDialog } = get();

    if (doiDialog.resolve) {
      doiDialog.resolve(result || null);
    }

    set({
      doiDialog: {
        isOpen: false,
      },
    });
  },

  closeDownloadFolderDialog: () => {
    log.debug('[DialogStore] Closing share folder dialog');
    const { downloadFolderDialog: DownloadFolderDialog } = get();

    if (DownloadFolderDialog.resolve) {
      DownloadFolderDialog.resolve();
    }

    set({
      downloadFolderDialog: {
        isOpen: false,
      },
    });
  },

  showBulkRecipeEditorDialog: async (
    activityId: number,
    activityType: 'preparation' | 'synthesis' | 'compounding',
    sample?: SampleDto,
    startingMaterialNumber?: 1 | 2,
    mode: 'view' | 'edit' = 'view',
    forceReadOnly?: boolean,
    startingMaterialSampleLabel?: string,
    viewingSampleLabel?: string,
    preparationOwnerRoles?: number[]
  ) => {
    log.debug('[DialogStore] Showing bulk recipe editor dialog', {
      activityId,
      activityType,
      startingMaterialNumber,
      mode,
      forceReadOnly,
      startingMaterialSampleLabel,
      viewingSampleLabel,
      preparationOwnerRoles,
    });

    return new Promise<InstrumentRunDto | SampleActivityDto | BulkSampleDto | null>((resolve, reject) => {
      set({
        bulkRecipeEditorDialog: {
          isOpen: true,
          activityId,
          activityType,
          sample,
          startingMaterialNumber,
          mode,
          forceReadOnly,
          startingMaterialSampleLabel,
          viewingSampleLabel,
          preparationOwnerRoles,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkRecipeEditorDialog: (result?: InstrumentRunDto | SampleActivityDto | BulkSampleDto | null) => {
    log.debug('[DialogStore] Closing bulk recipe editor dialog:', result);
    const { bulkRecipeEditorDialog } = get();

    if (bulkRecipeEditorDialog.resolve) {
      bulkRecipeEditorDialog.resolve(result || null);
    }

    set({
      bulkRecipeEditorDialog: {
        isOpen: false,
      },
    });
  },

  showBulkTransferDialog: async (samples: BulkSampleDto[], isCurrentProjectInHouse?: boolean) => {
    log.debug('[DialogStore] Showing bulk transfer dialog', { sampleCount: samples.length, isCurrentProjectInHouse });

    return new Promise<BulkTransferDialogResult | null>((resolve, reject) => {
      set({
        bulkTransferDialog: {
          isOpen: true,
          samples,
          isCurrentProjectInHouse,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkTransferDialog: (result?: BulkTransferDialogResult | null) => {
    log.debug('[DialogStore] Closing bulk transfer dialog:', result);
    const { bulkTransferDialog } = get();

    if (bulkTransferDialog.resolve) {
      bulkTransferDialog.resolve(result || null);
    }

    set({
      bulkTransferDialog: {
        isOpen: false,
      },
    });
  },

  showBulkGrowthDialog: async (config: BulkGrowthDialogConfig) => {
    log.debug('[DialogStore] Showing bulk growth dialog', { sampleCount: config.samples.length });

    return new Promise<BulkGrowthDialogResult | null>((resolve, reject) => {
      set({
        bulkGrowthDialog: {
          isOpen: true,
          samples: config.samples,
          title: config.title,
          availableTechniques: config.availableTechniques,
          availableInstrumentsGetter: config.availableInstrumentsGetter,
          initialProject: config.initialProject,
          initialTechnique: config.initialTechnique,
          validateBeforeSubmit: config.validateBeforeSubmit,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkGrowthDialog: (result?: BulkGrowthDialogResult | null) => {
    log.debug('[DialogStore] Closing bulk growth dialog:', result);
    const { bulkGrowthDialog } = get();

    if (bulkGrowthDialog.resolve) {
      bulkGrowthDialog.resolve(result || null);
    }

    set({
      bulkGrowthDialog: {
        isOpen: false,
      },
    });
  },

  showBulkSampleDialog: async (config: BulkSampleDialogConfig) => {
    log.debug('[DialogStore] Showing bulk sample dialog', config);

    return new Promise<BulkSampleDialogResult | null>((resolve, reject) => {
      set({
        bulkSampleDialog: {
          isOpen: true,
          title: config.title,
          initialProject: config.initialProject,
          canChangeProject: config.canChangeProject,
          showSampleID: config.showSampleID,
          showPrecompoundedSample: config.showPrecompoundedSample,
          hideRecipeTemplate: config.hideRecipeTemplate,
          initialRecipeTemplate: config.initialRecipeTemplate,
          initialDescription: config.initialDescription,
          initialSampleLabel: config.initialSampleLabel,
          initialTechnique: config.initialTechnique,
          availableRecipeTemplates: config.availableRecipeTemplates,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkSampleDialog: (result?: BulkSampleDialogResult | null) => {
    log.debug('[DialogStore] Closing bulk sample dialog:', result);
    const { bulkSampleDialog } = get();

    if (bulkSampleDialog.resolve) {
      bulkSampleDialog.resolve(result || null);
    }

    set({
      bulkSampleDialog: {
        isOpen: false,
      },
    });
  },

  showBulkInstrumentRunDialog: async (config: BulkInstrumentRunDialogConfig) => {
    log.debug('[DialogStore] Showing bulk instrument run dialog', config);

    return new Promise<BulkInstrumentRunDialogResult | null>((resolve, reject) => {
      set({
        bulkInstrumentRunDialog: {
          isOpen: true,
          title: config.title,
          initialTechnique: config.initialTechnique,
          initialInstrument: config.initialInstrument,
          sampleLabel: config.sampleLabel,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkInstrumentRunDialog: (result?: BulkInstrumentRunDialogResult | null) => {
    log.debug('[DialogStore] Closing bulk instrument run dialog:', result);
    const { bulkInstrumentRunDialog } = get();

    if (bulkInstrumentRunDialog.resolve) {
      bulkInstrumentRunDialog.resolve(result || null);
    }

    set({
      bulkInstrumentRunDialog: {
        isOpen: false,
      },
    });
  },

  showBulkShippingDialog: async (config: BulkShippingDialogConfig) => {
    log.debug('[DialogStore] Showing bulk shipping dialog', config);

    return new Promise<BulkShippingDialogResult | null>((resolve, reject) => {
      set({
        bulkShippingDialog: {
          isOpen: true,
          samples: config.samples,
          projectId: config.projectId,
          projectLabel: config.projectLabel,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkShippingDialog: (result?: BulkShippingDialogResult | null) => {
    log.debug('[DialogStore] Closing bulk shipping dialog:', result);
    const { bulkShippingDialog } = get();

    if (bulkShippingDialog.resolve) {
      bulkShippingDialog.resolve(result || null);
    }

    set({
      bulkShippingDialog: {
        isOpen: false,
      },
    });
  },

  showBulkShippingCreatedDialog: async (config: BulkShippingCreatedDialogConfig) => {
    log.debug('[DialogStore] Showing bulk shipping created dialog', config);

    return new Promise<BulkShippingCreatedChoice>((resolve, reject) => {
      set({
        bulkShippingCreatedDialog: {
          isOpen: true,
          isLocalTransfer: config.isLocalTransfer,
          canSubmit: config.canSubmit,
          submitDisabledReason: config.submitDisabledReason,
          resolve,
          reject,
        },
      });
    });
  },

  closeBulkShippingCreatedDialog: (choice: BulkShippingCreatedChoice = 'close') => {
    log.debug('[DialogStore] Closing bulk shipping created dialog:', choice);
    const { bulkShippingCreatedDialog } = get();

    if (bulkShippingCreatedDialog.resolve) {
      bulkShippingCreatedDialog.resolve(choice);
    }

    set({
      bulkShippingCreatedDialog: {
        isOpen: false,
      },
    });
  },

  showSubmitDialog: async (nodeType: string, nodeId: string) => {
    log.debug('[DialogStore] Showing submit dialog for:', nodeType, nodeId);

    return new Promise<boolean>((resolve, reject) => {
      set({
        submitDialog: {
          isOpen: true,
          nodeType,
          nodeId,
          resolve,
          reject,
        },
      });
    });
  },

  showResetDialog: async (nodeType: string, nodeId: string) => {
    log.debug('[DialogStore] Showing reset dialog for:', nodeType, nodeId);

    return new Promise<boolean>((resolve, reject) => {
      set({
        resetDialog: {
          isOpen: true,
          nodeType,
          nodeId,
          resolve,
          reject,
        },
      });
    });
  },

  showDeleteDialog: async (nodeType: string, nodeId: string) => {
    log.debug('[DialogStore] Showing delete dialog for:', nodeType, nodeId);

    return new Promise<boolean>((resolve, reject) => {
      set({
        deleteDialog: {
          isOpen: true,
          nodeType,
          nodeId,
          resolve,
          reject,
        },
      });
    });
  },

  closeSubmitDialog: (confirmed: boolean) => {
    log.debug('[DialogStore] Closing submit dialog:', confirmed);
    const { submitDialog } = get();

    if (submitDialog.resolve) {
      submitDialog.resolve(confirmed);
    }

    set({
      submitDialog: {
        isOpen: false,
      },
    });
  },

  closeResetDialog: (confirmed: boolean) => {
    log.debug('[DialogStore] Closing reset dialog:', confirmed);
    const { resetDialog } = get();

    if (resetDialog.resolve) {
      resetDialog.resolve(confirmed);
    }

    set({
      resetDialog: {
        isOpen: false,
      },
    });
  },

  closeDeleteDialog: (confirmed: boolean) => {
    log.debug('[DialogStore] Closing delete dialog:', confirmed);
    const { deleteDialog } = get();

    if (deleteDialog.resolve) {
      deleteDialog.resolve(confirmed);
    }

    set({
      deleteDialog: {
        isOpen: false,
      },
    });
  },
}));
